// ─── Template: lower-third-v1 ──────────────────────────────────────────────
// A broadcast-style lower third: a colored bar slides in from the left,
// title appears, subtitle slides up underneath. Designed for naming people
// or labeling moments in a video.

import React from 'react';
import { AbsoluteFill, Easing, interpolate, useCurrentFrame, useVideoConfig } from 'remotion';
import type { AnimationTemplate } from '../types';

export type AlignPreset = 'left' | 'center' | 'right';

const FONT_STACK = 'Inter, system-ui, -apple-system, "Segoe UI", Roboto, sans-serif';

export type LowerThirdProps = {
  title: string;
  subtitle: string;
  titleFont: string;
  titleSize: number;
  titleColor: string;
  titleBold: boolean;
  titleItalic: boolean;
  titleAlign: AlignPreset;
  subtitleFont: string;
  subtitleSize: number;
  subtitleColor: string;
  subtitleBold: boolean;
  subtitleItalic: boolean;
  subtitleAlign: AlignPreset;
  /** Bar color behind the title. */
  barColor: string;
  /** Bottom inset as % of composition height. */
  bottomInsetPct: number;
};

const Component: React.FC<LowerThirdProps> = ({
  title, subtitle,
  titleFont, titleSize, titleColor, titleBold, titleItalic, titleAlign,
  subtitleFont, subtitleSize, subtitleColor, subtitleBold, subtitleItalic, subtitleAlign,
  barColor, bottomInsetPct,
}) => {
  const frame = useCurrentFrame();
  const { width, height } = useVideoConfig();

  // Bar slides in from left. Title fades in slightly after, subtitle last.
  const barProgress = interpolate(frame, [0, 18], [0, 1], {
    easing: Easing.out(Easing.cubic),
    extrapolateRight: 'clamp',
  });
  const titleOp = interpolate(frame, [10, 22], [0, 1], { extrapolateRight: 'clamp' });
  const titleX = interpolate(frame, [10, 22], [-30, 0], {
    easing: Easing.out(Easing.cubic),
    extrapolateRight: 'clamp',
  });
  const subOp = interpolate(frame, [18, 30], [0, 1], { extrapolateRight: 'clamp' });
  const subY = interpolate(frame, [18, 30], [12, 0], {
    easing: Easing.out(Easing.cubic),
    extrapolateRight: 'clamp',
  });

  // The bar's height adapts to the title text size so the layout holds at
  // any composition resolution. Usamos `min(width, height)` para que el
  // tamaño visual sea consistente entre 16:9 y 9:16 — con `height` solo,
  // las letras crecen demasiado en formato reel.
  const refDim = Math.min(width, height);
  const titlePx = (titleSize / 100) * refDim;
  const subtitlePx = (subtitleSize / 100) * refDim;
  const barHeight = titlePx * 1.6;
  const barWidth = (0.55 + 0.05 * (Math.min(title.length, 40) / 40)) * width * barProgress;

  return (
    <AbsoluteFill style={{ background: 'transparent', fontFamily: FONT_STACK }}>
      {/* Bar */}
      <div
        style={{
          position: 'absolute',
          left: '6%',
          bottom: `${bottomInsetPct}%`,
          width: barWidth,
          height: barHeight,
          background: barColor,
          display: 'flex',
          alignItems: 'center',
          paddingLeft: barHeight * 0.4,
          paddingRight: barHeight * 0.6,
          overflow: 'hidden',
          // Soft shadow for legibility against busy footage.
          boxShadow: '0 4px 16px rgba(0,0,0,0.18)',
        }}
      >
        <span
          style={{
            color: titleColor,
            fontFamily: `${titleFont}, ${FONT_STACK}`,
            fontSize: titlePx,
            fontWeight: titleBold ? 800 : 500,
            fontStyle: titleItalic ? 'italic' : 'normal',
            textAlign: titleAlign,
            opacity: titleOp,
            transform: `translateX(${titleX}px)`,
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            width: '100%',
          }}
        >
          {title}
        </span>
      </div>
      {/* Subtitle below the bar */}
      {subtitle && (
        <div
          style={{
            position: 'absolute',
            left: '6%',
            bottom: `calc(${bottomInsetPct}% - ${barHeight * 0.7}px)`,
            paddingLeft: barHeight * 0.4,
            opacity: subOp,
            transform: `translateY(${subY}px)`,
          }}
        >
          <span
            style={{
              color: subtitleColor,
              fontFamily: `${subtitleFont}, ${FONT_STACK}`,
              fontSize: subtitlePx,
              fontWeight: subtitleBold ? 700 : 400,
              fontStyle: subtitleItalic ? 'italic' : 'normal',
              textAlign: subtitleAlign,
              textShadow: '0 1px 4px rgba(0,0,0,0.5)',
            }}
          >
            {subtitle}
          </span>
        </div>
      )}
    </AbsoluteFill>
  );
};

const ALIGN_OPTIONS = [
  { value: 'left',   label: 'Izquierda' },
  { value: 'center', label: 'Centro' },
  { value: 'right',  label: 'Derecha' },
];

export const lowerThirdTemplate: AnimationTemplate<LowerThirdProps> = {
  id: 'lower-third-v1',
  label: 'Barra inferior',
  description: 'Barra inferior con nombre y cargo, estilo presentación.',
  category: 'text',
  defaultDurationSec: 4,
  defaultProps: {
    title: 'Camila Pérez',
    subtitle: 'CEO @ Atlas',
    titleFont: 'Inter',
    titleSize: 4,
    titleColor: '#FFFFFF',
    titleBold: true,
    titleItalic: false,
    titleAlign: 'left',
    subtitleFont: 'Inter',
    subtitleSize: 2.4,
    subtitleColor: '#FFFFFF',
    subtitleBold: false,
    subtitleItalic: false,
    subtitleAlign: 'left',
    barColor: '#1644EB',
    bottomInsetPct: 12,
  },
  propsSchema: {
    title: {
      type: 'text', label: 'Nombre', placeholder: 'Nombre principal', agentExposed: true,
      styling: {
        font: 'titleFont', size: 'titleSize', color: 'titleColor',
        bold: 'titleBold', italic: 'titleItalic', align: 'titleAlign',
      },
    },
    subtitle: {
      type: 'text', label: 'Cargo / contexto', placeholder: 'Texto secundario', agentExposed: true,
      styling: {
        font: 'subtitleFont', size: 'subtitleSize', color: 'subtitleColor',
        bold: 'subtitleBold', italic: 'subtitleItalic', align: 'subtitleAlign',
      },
    },
    titleFont:   { type: 'font',    label: 'Tipografía' },
    titleSize:   { type: 'number',  label: 'Tamaño', min: 2, max: 12, step: 0.2, decimals: 1, unit: '%' },
    titleColor:  { type: 'color',   label: 'Color' },
    titleBold:   { type: 'boolean', label: 'Negrita' },
    titleItalic: { type: 'boolean', label: 'Cursiva' },
    titleAlign:  { type: 'select',  label: 'Alineación', options: ALIGN_OPTIONS },
    subtitleFont:   { type: 'font',    label: 'Tipografía' },
    subtitleSize:   { type: 'number',  label: 'Tamaño', min: 1.5, max: 8, step: 0.2, decimals: 1, unit: '%' },
    subtitleColor:  { type: 'color',   label: 'Color' },
    subtitleBold:   { type: 'boolean', label: 'Negrita' },
    subtitleItalic: { type: 'boolean', label: 'Cursiva' },
    subtitleAlign:  { type: 'select',  label: 'Alineación', options: ALIGN_OPTIONS },
    barColor:       { type: 'color',  label: 'Color de la barra' },
    bottomInsetPct: { type: 'number', label: 'Margen inferior', min: 2, max: 40, step: 1, decimals: 0, unit: '%' },
  },
  Component,
};
